/**
 * The one owner of what the add-in knows about the page: for every bookmark any
 * card or the drawer follows, the paragraph it names as Word holds it now and the
 * table that paragraph sits in, read in one Word read per document change.
 *
 * WHY one owner
 *   Every chat card used to read its own paragraphs and tables when Word reported
 *   a change, and the drawer read them all again on its own schedule. Two
 *   schedules meant two readings of one document: a card could show a table the
 *   drawer had not drawn yet, and twenty cards could hold Word's lock while a
 *   Go to waited. Here one read serves every follower, and every follower renders
 *   from the same page in the same update.
 *
 * HOW
 *   - a follower `watch`es the bookmarks it needs and `subscribe`s to the page;
 *   - the store follows the document's event stream (`DocumentEvents`: Word's
 *     paragraph events, or the typing detector on a host without them, the
 *     add-in's own body replacements, and the pane coming back into focus) and
 *     reads soon after each change, one read for a burst, standing aside while a
 *     write or the agent's whole-document read is pending;
 *   - a card's target is resolved here once: a bookmark whose paragraph sits in a
 *     table carries its table id, and that table is read with the page.
 */
import type { DocumentEvent } from "./document-events";
import type { PageCommentRead, PageParagraphRead, TableRead, WordDocument } from "./word-document";

/**
 * The page as the store last read it, shared by every follower.
 *
 * CASES, for a follower's bookmark
 *   - not in `coveredBookmarks`   -> the store has not read for it yet: wait
 *   - covered, not in `paragraphs` -> gone from the document: recover
 *   - in `paragraphs`             -> its paragraph and, by `tableId`, its table
 */
export interface DocumentPage {
    version: number;
    /** False until the first read has returned. */
    completed: boolean;
    coveredBookmarks: ReadonlySet<string>;
    paragraphs: ReadonlyMap<string, PageParagraphRead>;
    tables: ReadonlyMap<string, TableRead>;
    /** Every comment of the document by id as the last read listed them, resolved or not; a reply's target absent here was deleted. */
    commentById: ReadonlyMap<string, PageCommentRead>;
    /** How many reads in a row have failed; 0 after a read that returned. */
    readFailures: number;
    /** What the last failed read said; null after a read that returned. */
    lastReadError: string | null;
    /**
     * When the read this page holds began in Word, in milliseconds since the epoch.
     *
     * WHY
     *   A card's "Reverted" is judged against this page. A page whose read began before the
     *   card's Apply wrote still holds the paragraph as it read before the write, so it reads
     *   the card's change as taken back (`documentLandingOfCard`).
     *
     * EXAMPLE
     *   card applied at 1792770258472, page read began at 1792770257100 -> not judged on this page
     *   card applied at 1792770258472, page read began at 1792770259300 -> judged on this page
     *
     * NULL
     *   0 before the first read returns.
     */
    readStartedAtMs: number;
}

export const EMPTY_DOCUMENT_PAGE: DocumentPage = { version: 0, completed: false, coveredBookmarks: new Set(), paragraphs: new Map(), tables: new Map(), commentById: new Map(), readFailures: 0, lastReadError: null, readStartedAtMs: 0 };

/** Edits arrive in bursts; one read per burst keeps Word off the typing path. */
const CHANGE_READ_DEBOUNCE_MS = 400;
/** How long a read stands aside while foreground work holds Word before asking again. */
const FOREGROUND_RETRY_MS = 250;
/** A failed read is tried again after 1 s, then 2 s, 4 s, …, never longer than this. */
const FAILED_READ_FIRST_RETRY_MS = 1_000;
const FAILED_READ_LONGEST_RETRY_MS = 30_000;

/**
 * How long to wait before the next try after `failures` failed reads in a row.
 *
 * EXAMPLE
 *   failures 1 -> 1 000 ms, 2 -> 2 000 ms, 3 -> 4 000 ms, 6 -> 30 000 ms (capped)
 */
export function failedReadRetryMs(failures: number): number {
    return Math.min(FAILED_READ_FIRST_RETRY_MS * 2 ** Math.max(0, failures - 1), FAILED_READ_LONGEST_RETRY_MS);
}

export class DocumentPageStore {
    private readonly watchedBookmarksByOwner = new Map<string, string[]>();
    private readonly listeners = new Set<(page: DocumentPage) => void>();
    private current: DocumentPage = EMPTY_DOCUMENT_PAGE;
    private following = false;
    private readTimer: number | null = null;
    private readInFlight = false;
    private readQueued = false;

    constructor(private readonly word: WordDocument) {}

    get page(): DocumentPage {
        return this.current;
    }

    /**
     * Follows these bookmarks for `ownerKey` until the returned function is called.
     * A read is scheduled at once, so the next page covers them.
     *
     * WHY an owner key
     *   Two cards may follow the same bookmark; each names its own interest, so one
     *   unmounting does not stop the read for the other.
     *
     * EDGE CASE an owner that follows no bookmark
     *   A chat whose only cards are replies follows the document's comments alone;
     *   every read lists them, so its owner is read for like any other.
     */
    watch(ownerKey: string, bookmarkNames: string[]): () => void {
        this.watchedBookmarksByOwner.set(ownerKey, [...bookmarkNames]);
        this.ensureFollowing();
        this.scheduleRead(0);
        return () => {
            this.watchedBookmarksByOwner.delete(ownerKey);
        };
    }

    /** Calls the listener with the page at once and after every read; returns the unsubscribe. */
    subscribe(listener: (page: DocumentPage) => void): () => void {
        this.listeners.add(listener);
        listener(this.current);
        return () => {
            this.listeners.delete(listener);
        };
    }

    /** Reads the page soon: a Go to, a write the add-in made, anything that may have changed the document without a paragraph event. */
    touch(): void {
        this.scheduleRead(0);
    }

    /**
     * Follows the document's event stream once, from the first watch.
     *
     * CASES
     *   - Word reports paragraphs changed, added or deleted -> a read after the burst settles
     *   - the typing detector reports an edit               -> a read after the burst settles
     *   - the add-in replaced the body                      -> a read at once when the write is over
     *   - the user came back to the pane                    -> a read at once: a tracked deletion or a
     *                                                          comment on a card's paragraph raised no event
     *   - Word reports a content control added              -> no read: the event names no paragraph,
     *                                                          and the paragraph events that come with
     *                                                          new text schedule the read
     *
     * WHY the pane's focus reads the page
     *   Word raises no paragraph event when a comment is resolved or deleted. The user
     *   does that in the document and comes back to the pane to apply, so the read on
     *   the pane's focus shows a reply card's held-back target when the user looks at
     *   it; the reply writer checks the target again at the write.
     */
    private ensureFollowing(): void {
        if (this.following) return;
        this.following = true;
        this.word.events.subscribe((event: DocumentEvent) => {
            if (event.kind === "body_replaced") {
                if (event.phase === "end") this.scheduleRead(0);
                return;
            }
            if (event.kind === "pane_focused") {
                this.scheduleRead(0);
                return;
            }
            if (event.kind === "edited_under_cursor") {
                this.scheduleRead(CHANGE_READ_DEBOUNCE_MS);
                return;
            }
            if (event.kind === "word_change" && event.change.kind !== "content_control_added") this.scheduleRead(CHANGE_READ_DEBOUNCE_MS);
        });
    }

    /** One timer: a new schedule replaces the pending one, so a burst of changes reads once. */
    private scheduleRead(delayMs: number): void {
        if (this.readTimer !== null) window.clearTimeout(this.readTimer);
        this.readTimer = window.setTimeout(() => {
            this.readTimer = null;
            void this.readNow();
        }, delayMs);
    }

    private watchedBookmarks(): string[] {
        return Array.from(new Set(Array.from(this.watchedBookmarksByOwner.values()).flat()));
    }

    /**
     * One read of the page for every watched bookmark.
     *
     * CASES
     *   - no owner is watching               -> no read
     *   - owners watch no bookmark           -> a read of the comments alone, for reply cards
     *   - a read is in flight                -> this one runs after it
     *   - foreground work is pending in Word -> stand aside, ask again shortly
     *   - the read fails                     -> the page keeps what it had and says so
     *                                           (`lastReadError`, `readFailures`), every
     *                                           follower hears it, and the read is tried again
     *                                           after a backoff (`failedReadRetryMs`), so no
     *                                           card waits for the user's next edit
     */
    private async readNow(): Promise<void> {
        if (this.watchedBookmarksByOwner.size === 0) return;
        const bookmarkNames = this.watchedBookmarks();
        if (this.readInFlight) {
            this.readQueued = true;
            return;
        }
        this.readInFlight = true;
        try {
            if (this.word.pendingForegroundWork() > 0) {
                this.scheduleRead(FOREGROUND_RETRY_MS);
                return;
            }
            let readStartedAtMs = 0;
            /**
             * WHY the time is taken inside the read
             *   `withRead` waits while a write holds Word. Taken here, once the read holds Word, a
             *   read that queued behind Apply's write is stamped after the write, not before it.
             */
            const read = await this.word.withRead((session) => {
                readStartedAtMs = Date.now();
                return session.readPage(bookmarkNames);
            });
            this.current = {
                readStartedAtMs,
                version: this.current.version + 1,
                completed: true,
                coveredBookmarks: new Set(bookmarkNames),
                paragraphs: new Map(read.paragraphs.map((paragraph) => [paragraph.bookmarkName, paragraph])),
                tables: read.tables,
                commentById: new Map(read.documentComments.map((pageComment) => [pageComment.id, pageComment])),
                readFailures: 0,
                lastReadError: null,
            };
            for (const listener of Array.from(this.listeners)) listener(this.current);
        } catch (error) {
            const readFailures = this.current.readFailures + 1;
            this.current = { ...this.current, readFailures, lastReadError: error instanceof Error ? error.message : String(error) };
            for (const listener of Array.from(this.listeners)) listener(this.current);
            this.scheduleRead(failedReadRetryMs(readFailures));
            return;
        } finally {
            this.readInFlight = false;
            if (this.readQueued) {
                this.readQueued = false;
                this.scheduleRead(0);
            }
        }
    }
}
